import { useCallback, useRef } from 'react';
import { useScrollOverflow } from '@hooks/useScrollOverflow';

import type { ScrollCarouselProps, ScrollCarouselState, ScrollMode } from './ScrollCarousel.types';

/**
 * Find the index of the currently snapped child by measuring which child's
 * snap edge is closest to the container's snap reference point. Works after
 * any scroll source (arrows, trackpad, touch, scrollbar) because mandatory
 * snap guarantees the container is always at a valid snap position.
 */
function findActiveIndex(
  container: HTMLElement,
  snapAlign: ScrollCarouselProps['snapAlign'],
): number {
  const { scrollLeft, clientWidth, children } = container;
  let bestIdx = 0;
  let bestDist = Infinity;

  for (let i = 0; i < children.length; i++) {
    const child = children[i] as HTMLElement;
    let dist: number;

    if (snapAlign === 'center') {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const containerCenter = scrollLeft + clientWidth / 2;
      dist = Math.abs(childCenter - containerCenter);
    } else if (snapAlign === 'end') {
      const childEnd = child.offsetLeft + child.offsetWidth;
      const containerEnd = scrollLeft + clientWidth;
      dist = Math.abs(childEnd - containerEnd);
    } else {
      dist = Math.abs(child.offsetLeft - scrollLeft);
    }

    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }

  return bestIdx;
}

/**
 * Calculate the scroll position that places a child at the given snap alignment.
 * CSS scroll-snap mandatory will fine-tune the final landing, but we need to get
 * close enough that it snaps to the intended child rather than a neighbor.
 */
function calcScrollTarget(
  container: HTMLElement,
  child: HTMLElement,
  snapAlign: ScrollCarouselProps['snapAlign'],
): number {
  if (snapAlign === 'center') {
    return child.offsetLeft - (container.clientWidth - child.offsetWidth) / 2;
  }
  if (snapAlign === 'end') {
    return child.offsetLeft - container.clientWidth + child.offsetWidth;
  }
  return child.offsetLeft;
}

/**
 * Hook that wraps useScrollOverflow with programmatic carousel navigation.
 * CSS scroll-snap does the heavy lifting; this hook adds scrollPrev/scrollNext
 * and maps overflow state to carousel-friendly booleans.
 *
 * @param scrollMode - 'page' scrolls ~85% of viewport width, 'item' scrolls one child at a time
 * @param snapAlign - snap alignment, used by item mode to match scrollIntoView inline position
 */
export const useScrollCarousel = (
  scrollMode: ScrollMode = 'page',
  snapAlign: ScrollCarouselProps['snapAlign'] = 'start',
): ScrollCarouselState => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { hasOverflow, isScrolledLeft, isScrolledRight } = useScrollOverflow(scrollRef);

  const scrollPrev = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (scrollMode === 'item') {
      const idx = findActiveIndex(el, snapAlign);
      const target = el.children[Math.max(0, idx - 1)] as HTMLElement | undefined;
      if (target) {
        el.scrollTo({ left: calcScrollTarget(el, target, snapAlign), behavior: 'smooth' });
      }
    } else {
      el.scrollBy({ left: -(el.clientWidth * 0.85), behavior: 'smooth' });
    }
  }, [scrollMode, snapAlign]);

  const scrollNext = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (scrollMode === 'item') {
      const idx = findActiveIndex(el, snapAlign);
      const target = el.children[Math.min(el.children.length - 1, idx + 1)] as
        | HTMLElement
        | undefined;
      if (target) {
        el.scrollTo({ left: calcScrollTarget(el, target, snapAlign), behavior: 'smooth' });
      }
    } else {
      el.scrollBy({ left: el.clientWidth * 0.85, behavior: 'smooth' });
    }
  }, [scrollMode, snapAlign]);

  return {
    scrollRef,
    canScrollPrev: hasOverflow && isScrolledLeft,
    canScrollNext: hasOverflow && !isScrolledRight,
    hasOverflow,
    scrollPrev,
    scrollNext,
  };
};
